import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { store } from "../../redux/oldStore.tsx";
import { toast } from "react-toastify";

function queryParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch (e) {
    return null;
  }
}

function clearAuthQuery() {
  try {
    if (window.history && window.history.replaceState) {
      const url = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, url);
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * Shared top nav for Contests / Start pages.
 * Admin sign-in lives top-right (ticket: move off footer).
 */
export default function NhdNav({ user, onAuthChange, showNavLinks = true }) {
  const [showLogin, setShowLogin] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const token = queryParam("reset");
    if (token) {
      setResetToken(token);
      setMode("reset");
      setShowLogin(true);
      clearAuthQuery();
      return;
    }
    if (queryParam("sign-in") === "failed") {
      setMode("login");
      setShowLogin(true);
      toast.warning(
        "Google sign-in failed. Use your email and password, or ask an admin to link your Google account."
      );
      clearAuthQuery();
    }
  }, []);

  const closeModal = () => {
    setShowLogin(false);
    setMode("login");
    setPassword("");
    setPassword2("");
  };

  const signIn = () => {
    if (!email || email.indexOf("@") === -1) {
      toast.info("Please enter a valid email.");
      return;
    }
    if (!password || password.length < 4) {
      toast.info("Please enter a valid password.");
      return;
    }
    setBusy(true);
    // Contests/SignIn expects form-style fields
    store.server
      .postApi("Contests/SignIn", { email, password })
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          toast.success(r.Message || "Signed in");
          closeModal();
          if (onAuthChange) onAuthChange();
        } else {
          toast.warning((r && (r.Message || r.Data)) || "Invalid login");
        }
      })
      .catch(() => {
        setBusy(false);
        toast.error("Sign-in failed");
      });
  };

  const requestReset = () => {
    if (!email || email.indexOf("@") === -1) {
      toast.info("Please enter a valid email.");
      return;
    }
    setBusy(true);
    store.server
      .postApi("Contests/ForgotPassword", { email })
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          toast.success(
            (r && r.Message) ||
              "If that email is on file, we sent a password reset link."
          );
          setMode("login");
        } else {
          toast.warning((r && (r.Message || r.Data)) || "Could not send reset email");
        }
      })
      .catch(() => {
        setBusy(false);
        toast.error("Could not send reset email");
      });
  };

  const submitNewPassword = () => {
    if (!password || password.length < 4) {
      toast.info("Please enter a password that is at least 4 characters.");
      return;
    }
    if (password !== password2) {
      toast.info("Passwords do not match.");
      return;
    }
    setBusy(true);
    store.server
      .postApi("Contests/ResetPassword", { token: resetToken, password })
      .then((r) => {
        setBusy(false);
        if (r && r.Success) {
          toast.success(r.Message || "Password updated");
          closeModal();
          if (onAuthChange) onAuthChange();
        } else {
          toast.warning(
            (r && (r.Message || r.Data)) ||
              "This reset link is invalid or has expired."
          );
        }
      })
      .catch(() => {
        setBusy(false);
        toast.error("Could not reset password");
      });
  };

  const signOut = () => {
    store.server.postApi("Contests/SignOut", {}).then((r) => {
      if (r && r.Success) {
        toast.info("Signed out");
        if (onAuthChange) onAuthChange();
      }
    });
  };

  return (
    <div className="nhd-nav">
      <div className="nhd-nav-inner">
        <Link to="/" className="nhd-nav-brand">
          <img
            src="https://register.nhd.org/Assets/Images/nhdlogo.png"
            alt="NHD"
          />
          <span>National History Day Contests</span>
        </Link>

        {showNavLinks && (
          <div className="nhd-nav-links">
            <Link to="/app/Start">Quick Start Guide</Link>
            <Link to="/app/Contests">
              <i className="fad fa-search"></i> Find My Contest
            </Link>
          </div>
        )}

        <div className="nhd-nav-admin">
          {user ? (
            <div className="nhd-admin-user">
              <span className="nhd-admin-name">
                <i className="fad fa-user"></i> {user.firstName || user.email}
              </span>
              {user.securityLevel >= 100 && (
                <Link className="nhd-nav-link-btn" to="/app/Users">
                  Users
                </Link>
              )}
              <span className="nhd-nav-link-btn click" onClick={signOut}>
                Logout
              </span>
            </div>
          ) : (
            <span
              className="nhd-nav-link-btn click"
              onClick={() => {
                setMode("login");
                setShowLogin(true);
              }}
            >
              Admin Login
            </span>
          )}
        </div>
      </div>

      {showLogin && (
        <div className="nhd-modal-backdrop" onClick={closeModal}>
          <div
            className="nhd-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nhd-modal-header">
              <h3>
                {mode === "forgot"
                  ? "Reset Password"
                  : mode === "reset"
                  ? "Choose a New Password"
                  : "Sign In"}
              </h3>
              <span className="close click" onClick={closeModal}>
                ×
              </span>
            </div>
            <div className="nhd-modal-body">
              {mode === "login" && (
                <>
                  <div className="alert bg-warning text-dark">
                    Affiliate admin user login only
                  </div>
                  <div className="form-input">
                    <label htmlFor="nhd-login-email">E-Mail</label>
                    <input
                      id="nhd-login-email"
                      type="email"
                      maxLength={200}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="What is your email?"
                      autoComplete="username"
                      onKeyDown={(e) => e.key === "Enter" && signIn()}
                    />
                  </div>
                  <div className="form-input">
                    <label htmlFor="nhd-login-password">Password</label>
                    <input
                      id="nhd-login-password"
                      type="password"
                      maxLength={50}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      onKeyDown={(e) => e.key === "Enter" && signIn()}
                    />
                  </div>
                  <div className="nhd-modal-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={busy}
                      onClick={signIn}
                    >
                      {busy ? "Signing in…" : "Login"}
                    </button>
                    <a className="btn-secondary" href="../Login/Google">
                      Google
                    </a>
                  </div>
                  <div className="nhd-forgot-row">
                    <span
                      className="nhd-nav-link-btn click"
                      onClick={() => setMode("forgot")}
                    >
                      Forgot password?
                    </span>
                  </div>
                </>
              )}
              {mode === "forgot" && (
                <>
                  <p>
                    Enter the email on your affiliate admin account. If we find
                    it, we will send a reset link.
                  </p>
                  <div className="form-input">
                    <label htmlFor="nhd-forgot-email">E-Mail</label>
                    <input
                      id="nhd-forgot-email"
                      type="email"
                      maxLength={200}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="What is your email?"
                      autoComplete="username"
                      onKeyDown={(e) => e.key === "Enter" && requestReset()}
                    />
                  </div>
                  <div className="nhd-modal-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={busy}
                      onClick={() => setMode("login")}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={busy}
                      onClick={requestReset}
                    >
                      {busy ? "Sending…" : "Send reset link"}
                    </button>
                  </div>
                </>
              )}
              {mode === "reset" && (
                <>
                  <p>Choose a new password for your affiliate admin account.</p>
                  <div className="form-input">
                    <label htmlFor="nhd-reset-password">New password</label>
                    <input
                      id="nhd-reset-password"
                      type="password"
                      maxLength={50}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="New password"
                      autoComplete="new-password"
                      onKeyDown={(e) => e.key === "Enter" && submitNewPassword()}
                    />
                  </div>
                  <div className="form-input">
                    <label htmlFor="nhd-reset-password2">Confirm</label>
                    <input
                      id="nhd-reset-password2"
                      type="password"
                      maxLength={50}
                      value={password2}
                      onChange={(e) => setPassword2(e.target.value)}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                      onKeyDown={(e) => e.key === "Enter" && submitNewPassword()}
                    />
                  </div>
                  <div className="nhd-modal-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={busy}
                      onClick={submitNewPassword}
                    >
                      {busy ? "Saving…" : "Update password"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
